import React, { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import { useSite } from "../../../context/siteContext";
import { useTranslation } from "react-i18next";

// SunBurst Component
const SunBurst = ({ data }) => {
	const [chartData, setChartData] = useState([]);
	const [totalAmount, setTotalAmount] = useState(0);
	const { currencySymbol } = useSite();
	const { t } = useTranslation();
	// Helper: Truncate long names
	const truncateText = (text, length = 20) => (text.length > length ? `${text.slice(0, length)}...` : text);

	const formatCurrency = (value) => {
		const number = Number(value);

		if (isNaN(number)) return `${currencySymbol}0`;

		const absValue = Math.abs(number);
		let formattedValue;
		let suffix = "";

		if (absValue >= 1e9) {
			formattedValue = number / 1e9;
			suffix = "B";
		} else if (absValue >= 1e6) {
			formattedValue = number / 1e6;
			suffix = "M";
		} else if (absValue >= 1e3) {
			formattedValue = number / 1e3;
			suffix = "K";
		} else {
			formattedValue = number;
		}

		return `${currencySymbol}${formattedValue.toFixed(2)}${suffix}`;
	};

	const parseNumber = (value) => {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : 0;
	};

	// Process data into Sunburst format
	const generateSunburstData = (data) => {
		const siteMap = {};
		let total = 0;

		// Organize data: Site -> Orders -> Products (count) & Total
		data.forEach((item) => {
			const siteName = item.order.customer_site_number || "Unknown Site";
			const orderNumber = item.order.order_number || "Unknown Order";
			const productCount = Array.isArray(item.products) ? item.products.length : 0;
			const orderTotal = parseNumber(item.order.order_total);

			total += orderTotal; // Accumulate total for center

			// Ensure site exists
			if (!siteMap[siteName]) {
				siteMap[siteName] = {};
			}

			// Ensure order exists under site
			if (!siteMap[siteName][orderNumber]) {
				siteMap[siteName][orderNumber] = { productCount: 0, orderTotal: 0 };
			}

			// Update product count and total
			siteMap[siteName][orderNumber].productCount += productCount;
			siteMap[siteName][orderNumber].orderTotal += orderTotal;
		});

		// Convert to ECharts Sunburst format
		const sunburstData = Object.entries(siteMap).map(([site, orders]) => {
			const orderEntries = Object.entries(orders);
			const siteTotal = orderEntries.reduce((sum, [, { orderTotal }]) => sum + parseNumber(orderTotal), 0);
			return {
				name: truncateText(site),
				value: siteTotal,
				children: orderEntries.map(([orderNumber, { productCount, orderTotal }]) => ({
					name: truncateText(orderNumber),
					value: parseNumber(orderTotal),
					children: [
						{
							name: `${productCount} Products`,
							value: parseNumber(orderTotal),
						},
					],
				})),
			};
		});

		setChartData(sunburstData);
		setTotalAmount(total);
	};

	useEffect(() => {
		if (data?.length) {
			generateSunburstData(data);
		}
	}, [data]);

	// ECharts Sunburst Option
	const getOption = () => ({
		tooltip: {
			formatter: (params) => {
				if (params.data.name === t('TABLE_HEADERS.TOTAL')) {
					return `<b>${t('TABLE_HEADERS.TOTAL')}:</b> ${formatCurrency(params.data.value)}`;
				} else if (params.children && params.children.length > 0) {
					return `<b>${params.name}</b>`;
				} else {
					return `
            <b>${params.name}</b><br/>
            ${t('TABLE_HEADERS.TOTAL')}: ${formatCurrency(params.value)}
          `;
				}
			},
		},
		series: [
			{
				type: "sunburst",
				data: chartData,
				radius: [0, "90%"],
				label: {
					show: false, // Hide labels by default
					minAngle: 10, // Show labels for larger sections
					rotate: "tangential", // Ensure horizontal labels
					formatter: (params) => truncateText(params.name, 15), // Truncate long labels
				},
				itemStyle: {
					borderColor: "#fff",
					borderWidth: 2,
				},
				emphasis: {
					focus: "ancestor",
					label: {
						show: true, // Display label on hover
						formatter: (params) =>
							params.data.name === t('TABLE_HEADERS.TOTAL')
								? `${t('TABLE_HEADERS.TOTAL')}\n${formatCurrency(params.data.value)}`
								: `${params.name}\n${formatCurrency(params.value)}`,
						fontSize: 12,
						fontWeight: "bold",
					},
				},
			},
		],
	});

	return <ReactECharts option={getOption()} style={{ height: "100%", width: "100%" }} />;
};

export default SunBurst;
